import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';

import { SnackbarService } from '../../core/services';
import * as TodoActions from './todo.actions';
import { TodoApi } from './todo.api';
import type { ApiResponse, TodoItem } from './todo.models';

@Injectable()
export class TodoEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(TodoApi);
  private readonly snackbar = inject(SnackbarService);

  private toErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object') {
      if ('error' in error) {
        const nested = (error as { error?: { message?: unknown } }).error?.message;
        if (typeof nested === 'string') {
          return nested;
        }
      }
      if ('message' in error) {
        const message = (error as { message?: unknown }).message;
        if (typeof message === 'string') {
          return message;
        }
      }
    }
    return 'Unknown error';
  }

  fetchTodos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TodoActions.TodoItemsFetchRequest),
      switchMap(() =>
        this.api.listTodos().pipe(
          map((response: ApiResponse<TodoItem[]>) => TodoActions.TodoItemsFetchSuccess({ items: response.data ?? [] })),
          catchError((error) => of(TodoActions.TodoItemsFetchFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  createTodo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TodoActions.TodoCreateRequest),
      switchMap(({ payload }) =>
        this.api.createTodo(payload).pipe(
          map((response: ApiResponse<TodoItem>) => TodoActions.TodoCreateSuccess({ item: response.data })),
          catchError((error) => of(TodoActions.TodoCreateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  updateTodo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TodoActions.TodoUpdateRequest),
      switchMap(({ todoId, payload, previousItem }) =>
        this.api.updateTodo(todoId, payload).pipe(
          map((response: ApiResponse<TodoItem>) => TodoActions.TodoUpdateSuccess({ item: response.data })),
          catchError((error) => of(TodoActions.TodoUpdateFailure({ error: this.toErrorMessage(error), previousItem }))),
        ),
      ),
    ),
  );

  reorderTodo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TodoActions.TodoReorderRequest),
      switchMap(({ todoId, payload, previousItems }) =>
        this.api.reorderTodo(todoId, payload).pipe(
          map((response: ApiResponse<TodoItem[]>) => TodoActions.TodoReorderSuccess({ items: response.data ?? [] })),
          catchError((error) => of(TodoActions.TodoReorderFailure({ error: this.toErrorMessage(error), previousItems }))),
        ),
      ),
    ),
  );

  deleteTodo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TodoActions.TodoDeleteRequest),
      switchMap(({ todoId }) =>
        this.api.deleteTodo(todoId).pipe(
          map(() => TodoActions.TodoDeleteSuccess({ todoId })),
          catchError((error) => of(TodoActions.TodoDeleteFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  successNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TodoActions.TodoDeleteSuccess),
        map(() => {
          this.snackbar.showNotification('success', '', 'Todo deleted.', 'Close');
        }),
      ),
    { dispatch: false },
  );

  failureNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TodoActions.TodoItemsFetchFailure,
          TodoActions.TodoCreateFailure,
          TodoActions.TodoUpdateFailure,
          TodoActions.TodoReorderFailure,
          TodoActions.TodoDeleteFailure,
        ),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Todo error: ${error}`, 'Close');
        }),
      ),
    { dispatch: false },
  );
}
